import { useExerciseLibraryStatus } from '../hooks/useExerciseLibraryStatus';
import { getLibraryCapabilities } from '../lib/exerciseLibraryApi';
import BrandLogo from './BrandLogo';
import React, { useState, useEffect } from 'react';
import { useClubAccessRequests } from '../hooks/useClubAccessRequests';
import { AppIcons } from '../config/AppIcons';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Shield } from 'lucide-react';
import { useUserStore } from '../store/userStore';
import { useMatchStore } from '../store/matchStore';
import { useI18n } from '../i18n';
import TeamSwitcher from './sidebar/TeamSwitcher';
import QuickAction from './sidebar/QuickAction';
import SidebarItem from './sidebar/SidebarItem';
import { useAsyncQuery } from '../hooks/useAsyncQuery';
import { subscribeEmailQueueRefresh } from '../lib/adminEmailQueueEvents';
import { getAdminPlatformSummaryApi } from '../lib/adminDashboardApi';
import { isDevelopmentEnv } from '../utils/appEnv';
import { getDashboardMatchesApi } from '../lib/dashboardApi';
import { getLatestForumPostsApi } from '../lib/supportForumApi';
import { authenticatedFetch, getAuthHeaders } from '../lib/authApi';
import { useLivePresenceStore } from '../store/livePresenceStore';
import { MATCHES_CHANGED_EVENT } from '../lib/matchesApi';
import { getAuthenticatedSiteUrl, getSiteUrl } from '../utils/siteUrl';
import { canAccessOwnClubManagement } from '../utils/clubPermissions';
import { startOnlineUsersPolling, type OnlinePortalUser } from '../lib/adminPresenceApi';
import OnlineUsersPanel from './sidebar/OnlineUsersPanel';
import { listSeasonsApi, type Season } from '../lib/seasonsApi';
import { resolveOnlyTeamForSeason, resolveSelectedSeason, useSeasonStore } from '../store/seasonStore';

const Sidebar: React.FC = () => {
    const exerciseLibraryStatus = useExerciseLibraryStatus();
    const [exerciseAdministration, setExerciseAdministration] = useState(false);
    useEffect(() => {
      let active = true;
      if (exerciseLibraryStatus.available) void getLibraryCapabilities().then(value => {
        if (active) setExerciseAdministration(Boolean(value.can_assign_platform || value.grants.some(grant => grant.scope === 'platform')));
      }).catch(() => { if (active) setExerciseAdministration(false); });
      return () => { active = false; };
    }, [exerciseLibraryStatus.available]);
    const [collapsed, setCollapsed] = useState(false);
    const [isHubLoading, setIsHubLoading] = useState(false);
    const [showPresenceUsers, setShowPresenceUsers] = useState(false);
    const [presenceUsers, setPresenceUsers] = useState<OnlinePortalUser[] | null>(null);
    const [presenceUsersError, setPresenceUsersError] = useState(false);
    const { currentUser, logout, isAdmin, isClubManager, getCurrentTeam, isLoading, isInitialized, teams } = useUserStore();
    const { currentMatchId } = useMatchStore();
    const { t } = useI18n();
    const navigate = useNavigate();
    const portalUserCount = useLivePresenceStore(state => state.portalUserCount);
    const portalConnected = useLivePresenceStore(state => state.portalConnected);
    const selectedSeasonId = useSeasonStore(state => state.selectedSeasonId);
    const setSelectedSeasonId = useSeasonStore(state => state.setSelectedSeasonId);

    const currentTeam = getCurrentTeam();
    const hasTeam = !!currentTeam;
    const hasMatch = !!currentMatchId;
    const isDevEnv = isDevelopmentEnv();
    const isTrainer = Boolean(currentUser?.istrainer);
    const allCoachTeams = teams.filter(team =>
        team.coach.some(coach => coach.id === currentUser?.id)
    );
    const hasCoachTeams = allCoachTeams.length > 0;
    const isAdminOrClubManager = isAdmin() || isClubManager();
    const canAccessClubManagement = canAccessOwnClubManagement(currentUser);
    const accessRequests = useClubAccessRequests(canAccessClubManagement ? currentUser?.club_id ?? null : null, currentUser?.id);
    const hideCoachWorkspaceSections = isAdminOrClubManager && !hasCoachTeams;
    const isCoachForCurrentTeam = Boolean(
        currentUser?.id &&
        currentTeam?.coach?.some(coach => coach.id === currentUser.id)
    );
    const canAccessCoachTools = hasCoachTeams && (
        Boolean(currentUser?.iscoach) ||
        isCoachForCurrentTeam ||
        isAdmin() ||
        isClubManager()
    );

    const { data: loadedSeasons } = useAsyncQuery<Season[]>(
        async () => {
            try {
                return { data: await listSeasonsApi(), error: null };
            } catch (error) {
                return { data: [], error };
            }
        },
        [currentUser?.id],
        [],
        'Sidebar:seasons'
    );
    const seasons = loadedSeasons || [];
    const selectedSeason = resolveSelectedSeason(seasons, selectedSeasonId);

    useEffect(() => {
        if (selectedSeason && selectedSeason.id !== selectedSeasonId) {
            setSelectedSeasonId(selectedSeason.id);
        }
    }, [selectedSeason, selectedSeasonId, setSelectedSeasonId]);

    useEffect(() => {
        if (!isInitialized || isLoading || !selectedSeason) return;
        if (currentTeam?.seasonId === selectedSeason.id) return;
        const automaticTeamId = resolveOnlyTeamForSeason(
            allCoachTeams,
            selectedSeason.id,
            null,
        );
        if (automaticTeamId) {
            useUserStore.getState().setCurrentTeam(automaticTeamId);
        } else if (currentTeam && currentTeam.seasonId !== selectedSeason.id) {
            useUserStore.getState().setCurrentTeam('');
        }
    }, [allCoachTeams, currentTeam, selectedSeason, isInitialized, isLoading]);

    const asRecord = (value: unknown): Record<string, unknown> | null =>
        value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
    const asString = (value: unknown): string => {
        if (typeof value === 'string') return value;
        if (typeof value === 'number') return String(value);
        return '';
    };
    const buildMatchSignature = (dateRaw: string, homeTeam: string, awayTeam: string): string => {
        const date = new Date(dateRaw);
        if (Number.isNaN(date.getTime())) return '';
        return [
            date.toISOString().slice(0, 16),
            homeTeam.trim().toLowerCase(),
            awayTeam.trim().toLowerCase(),
        ].join('::');
    };

    const { data: planningCount = 0, reload: reloadPlanningCount } = useAsyncQuery<number>(
        async () => {
            if (!currentTeam?.id) return { data: 0, error: null };
            try {
                const now = new Date();
                const rows = await getDashboardMatchesApi();
                const localMatches = rows
                    .map((entry) => asRecord(entry))
                    .filter((row): row is Record<string, unknown> => !!row && asString(row.team_id) === currentTeam.id);

                let total = 0;
                const localSignatures = new Set<string>();

                for (const match of localMatches) {
                    const status = asString(match.status).toLowerCase();
                    if (status === 'completed') continue;

                    const dateRaw = asString(match.date);
                    const date = new Date(dateRaw);
                    if (Number.isNaN(date.getTime()) || date < now) continue;

                    const signature = buildMatchSignature(
                        dateRaw,
                        asString(match.home_team),
                        asString(match.away_team)
                    );
                    if (signature) {
                        if (localSignatures.has(signature)) continue;
                        localSignatures.add(signature);
                    }

                    total += 1;
                }

                return { data: total, error: null };
            } catch (error) {
                return { data: 0, error };
            }
        },
        [currentTeam?.id],
        0,
        'Sidebar:planningCount'
    );

    useEffect(() => {
        const handleMatchesChanged = () => {
            void reloadPlanningCount();
        };
        window.addEventListener(MATCHES_CHANGED_EVENT, handleMatchesChanged);
        return () => window.removeEventListener(MATCHES_CHANGED_EVENT, handleMatchesChanged);
    }, [reloadPlanningCount]);

    const totalPlanningCount =
        typeof planningCount === 'number' ? planningCount : 0;

    const roleLabelByRole: Record<string, string> = {
        admin: t('user_profile_role_admin'),
        'club-manager': t('user_profile_role_club_manager'),
        user: t('user_profile_role_user'),
    };
    const roleLabel = currentUser?.role ? (roleLabelByRole[currentUser.role] || currentUser.role) : '';
    const userBadgeLabel = [
        roleLabel,
        currentUser?.iscoach ? t('user_profile_is_coach') : '',
        currentUser?.istrainer ? t('user_profile_is_trainer') : '',
    ].filter((part) => typeof part === 'string' && part.trim().length > 0).join(' | ');
    const liveUsersCount = typeof portalUserCount === 'number' ? Math.max(0, portalUserCount) : null;
    const liveUsersBadgeLabel =
        liveUsersCount === null
            ? ''
            : t('sidebar_live_users_badge', { count: liveUsersCount });
    const liveUsersBadgeText =
        liveUsersCount === null
            ? ''
            : liveUsersCount > 99
                ? '99+'
                : String(liveUsersCount);
    const loadPresenceUsers = () => {
        if (!isAdmin()) return;
        setShowPresenceUsers(true);
    };
    const canReadPresenceUsers = isAdmin();
    useEffect(() => {
        setPresenceUsers(null);
        setPresenceUsersError(false);
        if (!showPresenceUsers || !canReadPresenceUsers) return;
        if (!portalConnected) {
            setPresenceUsersError(true);
            return;
        }
        return startOnlineUsersPolling(users => {
            setPresenceUsers(users);
            setPresenceUsersError(false);
        }, () => {
            setPresenceUsers(null);
            setPresenceUsersError(true);
        });
    }, [showPresenceUsers, canReadPresenceUsers, currentUser?.id, portalConnected]);
    const showGlobalLoading = isHubLoading || isLoading;
    const { data: eventCount = 0 } = useAsyncQuery<number>(
        async () => {
            if (!currentMatchId) return { data: 0, error: null };
            const baseUrl = import.meta.env.VITE_AI_API_URL?.replace(/\/$/, '');
            if (!baseUrl) return { data: 0, error: null };
            try {
                const response = await authenticatedFetch(
                    `${baseUrl}/api/v1/live/matches/${encodeURIComponent(currentMatchId)}/events?limit=100`,
                    {
                        method: 'GET',
                        credentials: 'include',
                        headers: getAuthHeaders(),
                    }
                );
                if (!response.ok) return { data: 0, error: null };
                const payload = await response.json();
                const data = Array.isArray(payload?.data) ? payload.data : [];
                return { data: data.length, error: null };
            } catch {
                return { data: 0, error: null };
            }
        },
        [currentMatchId],
        0,
        'Sidebar:eventCount'
    );
    const hasEvents = (eventCount ?? 0) > 0;
    const analysisDisabled = !hasTeam || !hasMatch || !hasEvents;

    const { data: forumPostCount = 0 } = useAsyncQuery<number>(
        async () => {
            try {
                const posts = await getLatestForumPostsApi(50);
                return { data: posts.length, error: null };
            } catch {
                return { data: 0, error: null };
            }
        },
        [],
        0,
        'Sidebar:forumPostCount'
    );

    const { data: queuedEmailCount = 0, reload: reloadQueuedEmailCount } = useAsyncQuery<number>(
        async () => {
            if (!isAdmin()) return { data: 0, error: null };
            const summary = await getAdminPlatformSummaryApi();
            return { data: summary.email.queued, error: null };
        },
        [currentUser?.id, currentUser?.role],
        0,
        'Sidebar:queuedEmailCount',
        true
    );
    const canReadEmailQueue = isAdmin();
    useEffect(() => {
        if (!canReadEmailQueue) return;
        return subscribeEmailQueueRefresh(reloadQueuedEmailCount);
    }, [canReadEmailQueue, reloadQueuedEmailCount]);
    const queuedEmailBadgeCount = typeof queuedEmailCount === 'number' ? queuedEmailCount : 0;

    // Handle logout
    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error("Logout failed", error);
        }
    };

    // Reclaim horizontal space for the content on compact laptop viewports.
    useEffect(() => {
        const handleResize = () => {
            setCollapsed(window.innerWidth < 1180);
        };
        window.addEventListener('resize', handleResize);
        handleResize(); // Initial check
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const handleHubLoading = (event: Event) => {
            const detail = (event as CustomEvent<{ isLoading?: boolean }>).detail;
            setIsHubLoading(Boolean(detail?.isLoading));
        };

        window.addEventListener('hub-loading-change', handleHubLoading as EventListener);
        return () => {
            window.removeEventListener('hub-loading-change', handleHubLoading as EventListener);
        };
    }, []);


    const GroupLabel = ({ label }: { label: string }) => {
        if (collapsed) return <div className="h-px bg-gray-200 my-2 mx-4" />;
        return (
            <div className="px-4 mt-6 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {label}
            </div>
        );
    };

    return (
        <aside
            className={clsx(
                'bg-white flex flex-col h-full transition-all duration-300 relative z-30',
                collapsed ? 'w-20' : 'w-64'
            )}
        >
            {showGlobalLoading && (
                <div
                    className="sidebar-loading-orbit pointer-events-none absolute left-full top-14 z-40"
                    aria-label={t('loading')}
                    title={t('loading')}
                >
                    <div className="relative flex h-5 w-5 items-center justify-center">
                        <span className="absolute inline-flex h-5 w-5 animate-ping rounded-full bg-primary-300/70" />
                        <span className="absolute inline-flex h-5 w-5 rounded-full border-2 border-white bg-primary-500 shadow-md shadow-primary-500/30" />
                        <span className="relative h-2 w-2 rounded-full bg-white" />
                    </div>
                </div>
            )}
            {/* 1. Header (Hamburger + Logo + Context) */}
            <div className="flex items-center px-4 h-14 flex-shrink-0">
                <button
                    onClick={() => setCollapsed(!collapsed)}
                    className="p-2 text-gray-500 hover:bg-gray-100 rounded-full mr-2 transition-colors focus:outline-none"
                    aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
                >
                    <AppIcons.menu size={24} />
                </button>

                {!collapsed && (
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <BrandLogo imageClassName="h-10" />
                            {isAdmin() && (
                                <span
                                    className="absolute -right-4 -bottom-1 inline-flex h-5 w-5 items-center justify-center rounded-full border border-orange-200 bg-white text-orange-500 shadow-sm"
                                    title={t('admin_mode_badge')}
                                    aria-label={t('admin_mode_badge')}
                                >
                                    <Shield size={11} strokeWidth={2.2} />
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {hasCoachTeams && (
                <div className="px-3 py-2">
                    <TeamSwitcher
                        collapsed={collapsed}
                        seasonName={selectedSeason?.name}
                        seasonId={selectedSeason?.id}
                    />
                </div>
            )}

            <div className="flex-grow overflow-x-hidden pb-4">
                {/* 2. Quick Action */}
                {!hideCoachWorkspaceSections && (
                    <div className="mt-2 px-2">
                        <QuickAction
                            collapsed={collapsed}
                            seasons={seasons}
                            selectedSeasonId={selectedSeason?.id || null}
                            onSelectSeason={setSelectedSeasonId}
                        />
                    </div>
                )}

                {/* 3. Navigation Groups */}

                {/* START */}
                {!hideCoachWorkspaceSections && (
                    <>
                        <GroupLabel label={t('sidebar_header_start')} />
                        <div className="space-y-1 px-2">
                            <SidebarItem
                                iconName="dashboard"
                                label={t('dashboard')}
                                to="/"
                                collapsed={collapsed}
                            />
                        </div>
                    </>
                )}

                {/* PLANNEN & ORGANISEREN */}
                {!hideCoachWorkspaceSections && (
                    <>
                        <GroupLabel label={t('sidebar_header_planning')} />
                        <div className="space-y-1 px-2">
                            {canAccessCoachTools && (
                                <SidebarItem
                                    iconName="planning"
                                    label={t('planning')}
                                    to="/match-planning"
                                    disabled={!hasTeam}
                                    badge={totalPlanningCount > 0 ? totalPlanningCount : undefined}
                                    collapsed={collapsed}
                                />
                            )}
                            {isDevEnv && isTrainer && (
                                <SidebarItem
                                    iconName="training_calendar"
                                    label={t('sidebar_training_calendar')}
                                    to="/training-calendar"
                                    disabled={!hasTeam}
                                    collapsed={collapsed}
                                />
                            )}
                        </div>
                    </>
                )}

                {exerciseLibraryStatus.enabled && <div className="space-y-1 px-2"><SidebarItem iconName="training_exercises" label={t('exercise_library_title')} to="/exercises" collapsed={collapsed} /></div>}
                {exerciseLibraryStatus.enabled && exerciseAdministration && !isAdmin() && <><GroupLabel label={t('sidebar_header_admin')} /><div className="space-y-1 px-2"><SidebarItem iconName="training_exercises" label={t('exercise_library_admin')} to="/admin-exercise-library" collapsed={collapsed} /></div></>}
                {/* TRAINING VOORBEREIDING */}
                {!hideCoachWorkspaceSections && isTrainer && (
                    <>
                        <GroupLabel label={t('sidebar_header_training_prep')} />
                        <div className="space-y-1 px-2">
                            {!exerciseLibraryStatus.enabled && <SidebarItem
                                iconName="training_exercises"
                                label={t('sidebar_training_exercises')}
                                to="/training-practices"
                                collapsed={collapsed}
                            />}
                            <SidebarItem
                                iconName="training_sessions"
                                label={t('sidebar_training_sessions')}
                                to="/training-sessions"
                                collapsed={collapsed}
                            />
                            <SidebarItem
                                iconName="training_exercise_plans"
                                label={t('sidebar_training_exercise_plans')}
                                to="/training-exercise-plans"
                                collapsed={collapsed}
                            />
                        </div>
                    </>
                )}

                {/* WEDSTRIJD VOORBEREIDEN */}
                {canAccessCoachTools && (
                    <>
                        <GroupLabel label={t('sidebar_header_match')} />
                        <div className="space-y-1 px-2">
                            <SidebarItem
                                iconName="lineup"
                                label={`1. ${t('lineup')}`}
                                to="/match-lineup"
                                disabled={!hasTeam || !currentMatchId}
                                collapsed={collapsed}
                            />
                            <SidebarItem
                                iconName="schedule"
                                label={`2. ${t('schedule')}`}
                                to="/match-team-schedule"
                                disabled={!hasTeam || !currentMatchId}
                                collapsed={collapsed}
                            />
                            <SidebarItem
                                iconName="analysis"
                                label={`3. ${t('analysis')}`}
                                to="/match-analysis"
                                disabled={analysisDisabled}
                                collapsed={collapsed}
                            />
                        </div>
                    </>
                )}

                {/* TEAM MANAGEN */}
                {canAccessCoachTools && (
                    <>
                        <GroupLabel label={t('sidebar_header_team')} />
                        <div className="space-y-1 px-2">
                            <SidebarItem
                                iconName="team"
                                label={t('team')}
                                to="/team-management"
                                disabled={!hasTeam}
                                collapsed={collapsed}
                            />
                            <SidebarItem
                                iconName="player_statistics"
                                label={t('sidebar_stats')}
                                to="/player-stats"
                                disabled={!hasTeam}
                                collapsed={collapsed}
                            />
                            <SidebarItem
                                iconName="intake"
                                label={t('sidebar_intake')}
                                to="/team-intake"
                                disabled={!hasTeam}
                                collapsed={collapsed}
                            />
                            <SidebarItem
                                iconName="player_evaluations"
                                label={t('sidebar_evaluations')}
                                to="/team-evaluation-distributions"
                                disabled={!hasTeam}
                                collapsed={collapsed}
                            />
                        </div>
                    </>
                )}

                {/* CLUB MANAGEMENT */}
                {canAccessClubManagement && (
                    <>
                        <GroupLabel label={t('sidebar_header_club')} />
                        <div className="space-y-1 px-2">
                            <SidebarItem
                                iconName="club_dashboard"
                                label={t('sidebar_club_overview')}
                                to="/club-management"
                                collapsed={collapsed}
                            />
                            <SidebarItem iconName="users" label={t('club_section_users')} to="/club-users"
                                badge={accessRequests.requests.length || undefined} collapsed={collapsed} />
                            <SidebarItem iconName="teams" label={t('teams')} to="/teams?scope=club" collapsed={collapsed} />
                            <SidebarItem iconName="players" label={t('players')} to="/players" collapsed={collapsed} />
                            <SidebarItem iconName="training_exercises" label={t('club_section_exercises')} to="/club-exercises" collapsed={collapsed} />
                            <SidebarItem iconName="clubs" label={t('club_section_details')} to="/club-details" collapsed={collapsed} />
                        </div>
                    </>
                )}

                {/* ADMIN SECTION (Merged from AdminMenu) */}
                {isAdmin() && (
                    <>
                        <GroupLabel label={t('sidebar_header_admin')} />
                        <div className="space-y-1 px-2">
                            {isAdmin() && (
                                <>
                                    <SidebarItem iconName="admin_dashboard" label={t('sidebar_admin_dashboard')} to="/dashboard" collapsed={collapsed} />
                                    <SidebarItem
                                        iconName="admin_emails"
                                        label={t('admin_emails')}
                                        to="/admin-email"
                                        badge={queuedEmailBadgeCount > 0 ? queuedEmailBadgeCount : '-'}
                                        collapsed={collapsed}
                                    />
                                </>
                            )}
                            {isAdmin() && (
                                <>
                                    <SidebarItem iconName="clubs" label={t('sidebar_clubs')} to="/clubs" collapsed={collapsed} />
                                </>
                            )}
                            {isAdmin() && (
                                <>
                                    <SidebarItem iconName="users" label={t('users')} to="/admin-users" collapsed={collapsed} />
                                    <SidebarItem iconName="contacts" label={t('contacts')} to="/admin-contacts" collapsed={collapsed} />
                                    {exerciseLibraryStatus.enabled && exerciseAdministration && <SidebarItem iconName="training_exercises" label={t('exercise_library_admin')} to="/admin-exercise-library" collapsed={collapsed} />}
                                    <SidebarItem iconName="formations" label={t('formations')} to="/admin-formations" collapsed={collapsed} />
                                    <SidebarItem iconName="evaluations" label={t('evaluations')} to="/admin-evaluations" collapsed={collapsed} />
                                    <SidebarItem iconName="match_types" label={t('match_types')} to="/admin-matchtypes" collapsed={collapsed} />
                                    <SidebarItem iconName="seasons" label={t('seasons')} to="/admin-seasons" collapsed={collapsed} />
                                    <SidebarItem iconName="security" label={t('security_codes_menu')} to="/admin-security-codes" collapsed={collapsed} />
                                    <SidebarItem iconName="webdav" label="WebDAV" to="/admin-webdav" collapsed={collapsed} />
                                    <SidebarItem iconName="push_registrations" label={t('push_registrations_title')} to="/admin-push" collapsed={collapsed} />
                                    <SidebarItem iconName="database" label="Database" to="/admin-database" collapsed={collapsed} />
                                    {isDevEnv && (
                                        <SidebarItem iconName="security" label={t('admin_decrypt_menu')} to="/admin-decrypt-value" collapsed={collapsed} />
                                    )}
                                </>
                            )}
                        </div>
                    </>
                )}

                {/* OVERIG */}
                <GroupLabel label={t('sidebar_header_other')} />
                <div className="space-y-1 px-2">
                    <SidebarItem
                        iconName="support"
                        label={t('sidebar_support')}
                        to="#"
                        onClick={(e) => {
                            e.preventDefault();
                            window.open(getAuthenticatedSiteUrl('/support'), '_blank', 'noopener,noreferrer');
                        }}
                        collapsed={collapsed}
                    />
                    <SidebarItem
                        iconName="knowledge_base"
                        label={t('sidebar_knowledge_base')}
                        to="#"
                        onClick={(e) => {
                            e.preventDefault();
                            window.open(getSiteUrl('/support/docs'), '_blank', 'noopener,noreferrer');
                        }}
                        collapsed={collapsed}
                    />
                    <SidebarItem
                        iconName="forum"
                        label={t('sidebar_forum')}
                        to="#"
                        badge={forumPostCount ?? 0}
                        onClick={(e) => {
                            e.preventDefault();
                            window.open(getAuthenticatedSiteUrl('/support/forum'), '_blank', 'noopener,noreferrer');
                        }}
                        collapsed={collapsed}
                    />
                </div>
            </div>

            {/* 4. Footer (Floating at bottom) */}
            <div className="mt-auto border-t bg-gray-50 p-2">
                <div
                    className={clsx(
                        "relative flex items-center p-2 rounded-lg bg-white border border-gray-100",
                        collapsed ? "justify-center" : "justify-between"
                    )}
                >
                    <button
                        type="button"
                        onClick={() => navigate('/profile')}
                        className={clsx(
                            "flex items-center overflow-hidden rounded-md transition-colors hover:bg-orange-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400",
                            collapsed ? "justify-center p-1" : "flex-1 p-1 -m-1"
                        )}
                        title={collapsed ? t('user_profile_title') : undefined}
                        aria-label={t('user_profile_title')}
                    >
                        <div className="h-8 w-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-bold flex-shrink-0">
                            {currentUser?.displayName?.charAt(0) || 'U'}
                        </div>
                        {!collapsed && (
                            <>
                                <div className="ml-3 overflow-hidden text-left">
                                    <div className="text-sm font-medium text-gray-900 truncate flex items-center gap-2">
                                        {currentUser?.displayName}
                                    </div>
                                    {userBadgeLabel && <div className="text-xs text-gray-500 truncate">{userBadgeLabel}</div>}
                                    <div className="text-xs text-gray-500 truncate">{currentUser?.email}</div>
                                </div>
                            </>
                        )}
                    </button>
                    {liveUsersCount !== null && (
                        <div
                            className="absolute -right-1 -top-1"
                            onMouseEnter={() => { if (isAdmin()) void loadPresenceUsers(); }}
                            onMouseLeave={() => setShowPresenceUsers(false)}
                            onFocus={() => { if (isAdmin()) void loadPresenceUsers(); }}
                            onBlur={(event) => {
                                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                                    setShowPresenceUsers(false);
                                }
                            }}
                        >
                        <button
                            type="button"
                            onClick={() => {
                                if (!isAdmin()) return;
                                if (showPresenceUsers) setShowPresenceUsers(false);
                                else void loadPresenceUsers();
                            }}
                            disabled={!isAdmin()}
                            className={clsx(
                                "flex h-6 min-w-6 items-center justify-center gap-1 rounded-full border-2 border-white px-1.5 text-[10px] font-semibold shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300",
                                portalConnected
                                    ? "bg-gray-500 text-white"
                                    : "bg-gray-200 text-gray-700",
                                isAdmin() ? "cursor-pointer hover:bg-primary-500" : "cursor-default"
                            )}
                            title={liveUsersBadgeLabel}
                            aria-label={liveUsersBadgeLabel}
                            aria-expanded={isAdmin() ? showPresenceUsers : undefined}
                        >
                            <AppIcons.players size={10} />
                            {liveUsersBadgeText}
                        </button>
                        {isAdmin() && showPresenceUsers && (
                            <div className="absolute bottom-full left-0 z-50 w-80 max-w-[calc(100vw-6rem)] pb-2" role="status">
                                <div className="rounded-md border border-gray-200 bg-white p-3 text-left shadow-sm">
                                    <OnlineUsersPanel users={presenceUsers} error={presenceUsersError} />
                                </div>
                            </div>
                        )}
                        </div>
                    )}
                    {!collapsed && (
                        <div className="ml-2 flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => navigate('/profile')}
                                className="text-gray-400 hover:text-orange-500"
                                title={t('user_profile_title')}
                                aria-label={t('user_profile_title')}
                            >
                                <AppIcons.settings size={16} />
                            </button>
                            <button
                                type="button"
                                onClick={handleLogout}
                                className="text-gray-400 hover:text-red-500"
                                title={t('logout')}
                                aria-label={t('logout')}
                            >
                                <AppIcons.logout size={16} />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </aside>
    );
};

export default Sidebar;
